import React, { useContext, useState } from 'react';
import type {
  Candidate,
  CandidateProfileIssues,
  Experience,
} from '@axiom/validation';
import { ExperienceEditSchema } from '@axiom/validation';
import { ProfileCompletionConst } from '@axiom/const';
import {
  Button,
  CondensedLarge,
  Flashy,
  FluidButtonLayout,
  Form,
  Gutter,
  Icon,
  Layout,
  Textarea,
  ToastUtil,
} from '@axiom/ui';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { ExperienceApi } from '../../api/experience-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { EnvUtil } from '../../utils/env-util';

import {
  ExperienceEditSectionKeys,
  ExperienceEditUtil,
} from './experience-edit-util';
import type { ExperienceSectionStateType } from './ExperienceEditContext';
import { ExperienceEditContext } from './ExperienceEditContext';
import { ExperienceEditSection } from './ExperienceEditSection';
import { ExperienceEditIssue } from './ExperienceEditIssue';
import { ExperienceEditWorkDetailsSchema } from './experience-edit-work-details-schema';
import { ExperienceEditDescriptionLengthWarningModal } from './ExperienceEditDescriptionLengthWarningModal';

const { WORK_DETAILS, FOCUS_AREAS } = ExperienceEditSectionKeys;

export type ExperienceEditFormWorkDetailsProps = {
  candidate: Candidate;
  expandable?: boolean;
  experience: Experience;
  issues: CandidateProfileIssues['issues'];
  name?: string;
};

export const ExperienceEditFormWorkDetails = ({
  candidate,
  expandable,
  experience,
  issues,
  name = 'EXPERIENCE_EDIT_FORM_WORK_DETAILS',
}: ExperienceEditFormWorkDetailsProps) => {
  const [showModal, setShowModal] = useState(false);
  const [hasContinued, setHasContinued] = useState(false);
  const [hasRequestedException, setHasRequestedException] = useState(false);
  const {
    dirtyFormState,
    setDirtyFormState,
    accordionState,
    setAccordionState,
  } = useContext(ExperienceEditContext);
  const descriptionGuidanceIssue =
    CandidateProfileIssuesUtils.getExperienceDescriptionGuidanceIssue(
      issues,
      experience.id
    );
  const talentPartner = CandidateUtil.getTalentPartnerUser(candidate);

  return (
    <Form
      name={name}
      schema={ExperienceEditWorkDetailsSchema}
      initialValues={{
        description: experience.description || undefined,
      }}
      onSubmit={async formData => {
        if (
          !experience.exceptionRequestedAt &&
          talentPartner.email !== EnvUtil.axiomAnswersEmail &&
          formData.description &&
          formData.description.length < experience.expectedDescriptionLength &&
          !hasRequestedException &&
          !hasContinued
        ) {
          setShowModal(true);
          return;
        }
        const submitData = {
          candidateId: candidate.id,
          ...formData,
          ...(hasRequestedException ? { exceptionRequested: true } : {}),
        };
        await ExperienceApi.updateExperience(
          experience.id,
          ExperienceEditSchema.partial().parse(submitData)
        );
        await Promise.all([
          CandidateExperienceApi.refreshExperiences(candidate.id),
          CandidateExperienceApi.refreshGroupedExperiences(candidate.id),
          CandidateProfileIssuesApi.refreshCandidateProfileIssues(candidate.id),
        ]);
        setHasContinued(false);
        setHasRequestedException(false);
        setAccordionState({
          ...ExperienceEditUtil.setAllToFalse(accordionState),
          [FOCUS_AREAS]: true,
        });
      }}
    >
      {({ dirty, fireSubmit }) => {
        // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
        setTimeout(() => {
          if (dirtyFormState[WORK_DETAILS] !== dirty) {
            setDirtyFormState((prevState: ExperienceSectionStateType) => ({
              ...prevState,
              [WORK_DETAILS]: dirty,
            }));
          }
        }, 0);

        return (
          <ExperienceEditSection
            expandable={expandable}
            expanded={accordionState[WORK_DETAILS]}
            isComplete={ExperienceEditUtil.isWorkDetailsComplete(
              experience,
              issues
            )}
            title="Work Details"
            name={WORK_DETAILS}
          >
            <div data-test="EXPERIENCE_EDIT_FORM_WORK_DETAILS_BODY">
              {CandidateProfileIssuesUtils.experienceHasTooLongDescription(
                experience
              ) && (
                <Gutter bottom="16px">
                  <ExperienceEditIssue
                    title={
                      ProfileCompletionConst
                        .ProfileCompletionExperienceDescriptionBanner
                        .tooLongTitle
                    }
                    description={
                      ProfileCompletionConst
                        .ProfileCompletionExperienceDescriptionBanner
                        .tooLongContent
                    }
                    estimatedTime="10 min"
                    name="EXPERIENCE_EDIT_SHORTEN_DESCRIPTION"
                  />
                </Gutter>
              )}
              {descriptionGuidanceIssue ? (
                <Gutter bottom="16px">
                  <ExperienceEditIssue
                    title={
                      ProfileCompletionConst
                        .ExperienceDescriptionBannerConfigByCandidateProfileIssueContext[
                        descriptionGuidanceIssue.context
                      ]?.title as string
                    }
                    description={
                      ProfileCompletionConst
                        .ExperienceDescriptionBannerConfigByCandidateProfileIssueContext[
                        descriptionGuidanceIssue.context
                      ]?.description as string
                    }
                    estimatedTime={
                      ProfileCompletionConst
                        .ExperienceDescriptionBannerConfigByCandidateProfileIssueContext[
                        descriptionGuidanceIssue.context
                      ]?.estimatedTime as string
                    }
                    name="EXPERIENCE_EDIT_DESCRIPTION_GUIDANCE"
                  />
                </Gutter>
              ) : null}
              <Textarea
                name="description"
                label="Description of work"
                full
                formatting="modern"
                enableSuggestionsFor="portal-ExperienceItemNonAxiomEdit-description"
              />
              <Gutter bottom="16px" />
              {experience.exceptionRequestedAt &&
              !experience.exceptionApprovedAt ? (
                <Gutter bottom="16px">
                  <Flashy color="dataVizYellow">
                    <Layout
                      horizontalGutter="8px"
                      position="right middle"
                      name="EXPERIENCE_EDIT_EXCEPTION_REQUESTED"
                    >
                      <Flashy color="textDefault">Exception requested</Flashy>
                      <Icon name="circle-waiting-filled" />
                    </Layout>
                  </Flashy>
                </Gutter>
              ) : null}
              <FluidButtonLayout>
                <Button
                  pattern="secondary"
                  onClick={fireSubmit}
                  name="EXPERIENCE_EDIT_FORM_WORK_DETAILS_SAVE"
                >
                  Save
                </Button>
              </FluidButtonLayout>
            </div>
            {showModal ? (
              <ExperienceEditDescriptionLengthWarningModal
                candidate={candidate}
                experience={experience}
                name="EXPERIENCE_EDIT_DESCRIPTION_LENGTH_WARNING_MODAL"
                onClose={() => {
                  setShowModal(false);
                }}
                onContinue={async () => {
                  setHasContinued(true);
                  setShowModal(false);
                  await fireSubmit();
                }}
                onRequestException={async () => {
                  setHasRequestedException(true);
                  setShowModal(false);
                  await fireSubmit();
                  ToastUtil.add({
                    name: 'EXPERIENCE_DESCRIPTION_LENGTH_EXCEPTION_REQUESTED_TOAST',
                    children: (
                      <CondensedLarge>
                        Your exception request has been submitted.
                      </CondensedLarge>
                    ),
                  });
                }}
              />
            ) : null}
          </ExperienceEditSection>
        );
      }}
    </Form>
  );
};
